<script lang="ts" setup>
import ErrorDisplayConcise from "~/components/ErrorDisplayConcise.vue";
import type {SpecialistResults} from "~/types/mgnl";
import SpecialistCard from "~/components/SpecialistCard.vue";
import GoogleMap from "~/components/maps/GoogleMap.client.vue";
import PlacesAutoComplete from "~/components/maps/PlacesAutoComplete.vue";
import type {SpecialistMarker} from "~/types/common";
import type {GeocoderResultLiteral, HydratedSpecialist} from "~/types/geocoded";

const {t} = useI18n();
const runtimeConfig = useRuntimeConfig();

const {data, error} = await useMagnoliaFetch(
  runtimeConfig.public.mgnl.apiSpecialists,
  {
    transform: (node: SpecialistResults): HydratedSpecialist[] => {
      const specialists = node.results.map(
        (specialist): HydratedSpecialist => {
          if (!specialist.address?.geocoding) {
            return specialist as unknown as HydratedSpecialist;
          }
          try {
            const literal = JSON.parse(
              specialist.address.geocoding as unknown as string,
            ) as GeocoderResultLiteral;
            return {
              ...specialist,
              address: {
                ...specialist.address,
                geocoding: literal,
              },
            } as unknown as HydratedSpecialist;
          } catch (e) {
            return specialist as unknown as HydratedSpecialist;
          }
        },
      );
      specialists.sort((a, b) => (a.company ?? "").localeCompare(b.company ?? ""));
      return specialists;
    },
  },
);

console.log(data, error);

useErrorStatusForwarding(error);

const selectedSpecialist = ref<HydratedSpecialist>();
const activeMarker = ref<SpecialistMarker>();
const visibleMarkers = ref<SpecialistMarker[]>([]);

const place = ref<google.maps.GeocoderResult | GeocoderResultLiteral>();
const onPlaceSelected = (
  result: google.maps.GeocoderResult | GeocoderResultLiteral | undefined,
) => {
  place.value = result;
};

const specialists = computed(() => data.value || []);
const visibleMarkerIds = computed(() =>
  visibleMarkers.value.map((marker) => marker.specialist["@id"]),
);
const visibleSpecialists = computed(() => {
  let filtered = specialists.value.filter((specialist) =>
    visibleMarkerIds.value.includes(specialist["@id"]),
  );
  if (filtered.length === 0) {
    filtered = specialists.value.filter(
      (specialist) => specialist.countryFallback,
    );
  }
  if (activeMarker.value) {
    const index = filtered.findIndex(
      (specialist) =>
        specialist["@id"] === activeMarker.value?.specialist["@id"],
    );
    if (index >= 0) {
      filtered.splice(index, 1);
    }
    filtered.unshift(activeMarker.value.specialist);
  }
  return filtered;
});
const onVisible = (markers: SpecialistMarker[]) => {
  visibleMarkers.value = markers;
};
const onClicked = (marker: SpecialistMarker) => {
  activeMarker.value = marker;
};
const onSelected = (specialist: HydratedSpecialist) => {
  selectedSpecialist.value = specialist;
  onPlaceSelected(specialist.address?.geocoding);
};
const isSelectedSpecialist = (specialist: HydratedSpecialist) =>
  activeMarker.value?.specialist["@id"] === specialist["@id"];
</script>

<template>
  <section class="inner-content-grid mt-32 mb-22">
    <h1 class="sr-only">{{ t("label.overview") }}</h1>
    <ErrorDisplayConcise v-if="error" :error></ErrorDisplayConcise>
    <GoogleMap
      :place="place"
      :selected="selectedSpecialist"
      :specialists="specialists"
      class="col-span-full lg:col-span-4 bg-neutral-400 rounded-sm overflow-hidden !w-full h-[400px] sm:w-[400px] aspect-auto sm:!aspect-[640/400]"
      style="aspect-ratio: auto"
      @clicked="onClicked"
      @visible="onVisible" />
    <div
      v-if="data"
      class="col-span-full lg:col-span-8 flex flex-col gap-3 mt-3 lg:mt-0">
      <PlacesAutoComplete @autocomplete="onPlaceSelected" />
      <template
        v-for="specialist in visibleSpecialists"
        :key="specialist['@id']">
        <SpecialistCard
          :selected="isSelectedSpecialist(specialist)"
          :specialist
          @selected="onSelected" />
        <hr class="border-neutral-600 last:hidden" />
      </template>
    </div>
  </section>
</template>
